import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";
import { getInstallations } from "../../services/fireSafetyLogbookApi";
import {
  getCustomerPortalDashboard,
  getCustomerPortalWorkorders,
  toPortalErrorMessage,
} from "../../services/customerPortalApi";
import { useUser } from "../../context/UserContext";

const CARD_CLASS =
  "rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_8px_20px_rgba(15,23,42,0.05)]";

const OPEN_WORKORDER_STATUSES = new Set([
  "concept",
  "gereed_voor_planning",
  "gepland",
  "in_uitvoering",
  "wacht_op_materiaal",
  "wacht_op_goedkeuring",
]);

export default function PortalDashboardPage() {
  const { user } = useUser();
  const accountType = String(user?.accountType || "internal").trim().toLowerCase();
  const isCustomerPortalUser = accountType === "customer_portal";

  const [installations, setInstallations] = useState([]);
  const [workorders, setWorkorders] = useState([]);
  const [portalSummary, setPortalSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");

        if (isCustomerPortalUser) {
          const [summaryRes, listRes] = await Promise.all([
            getCustomerPortalDashboard(),
            getCustomerPortalWorkorders({ limit: 5 }),
          ]);

          if (!active) return;
          setPortalSummary(summaryRes?.summary || null);
          setWorkorders(Array.isArray(listRes?.items) ? listRes.items : []);
          setInstallations([]);
          return;
        }

        const [installationsList, workordersList] = await Promise.all([
          getInstallations(),
          loadInternalWorkorders(),
        ]);

        if (!active) return;
        setInstallations(Array.isArray(installationsList) ? installationsList : []);
        setWorkorders(Array.isArray(workordersList) ? workordersList : []);
        setPortalSummary(null);
      } catch (err) {
        if (!active) return;
        setError(
          isCustomerPortalUser
            ? toPortalErrorMessage(err, "Klantportaal dashboard laden mislukt")
            : err?.response?.data?.message || "KlantHub dashboard laden mislukt"
        );
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [isCustomerPortalUser]);

  const summary = useMemo(() => {
    if (isCustomerPortalUser) {
      const open = Number(portalSummary?.openWorkorders || 0);
      const done = Number(portalSummary?.completedWorkorders || 0);
      const installationCount = Number(portalSummary?.installations || 0);
      return {
        installationCount,
        locationCount: "-",
        openWorkorders: open,
        doneWorkorders: done,
      };
    }

    const installationCount = installations.length;
    const locationCount = new Set(
      installations
        .map((item) => String(item?.locatie || item?.bagAddress?.formattedAddress || "").trim())
        .filter(Boolean)
    ).size;
    const publishedWorkorders = workorders.filter(isWorkorderVisibleForClientHub);
    const openWorkorders = publishedWorkorders.filter((item) => {
      const status = String(item?.status || "").trim().toLowerCase();
      return OPEN_WORKORDER_STATUSES.has(status);
    }).length;
    const doneWorkorders = publishedWorkorders.filter((item) => {
      const status = String(item?.status || "").trim().toLowerCase();
      return status === "afgerond" || status === "gesloten";
    }).length;

    return {
      installationCount,
      locationCount,
      openWorkorders,
      doneWorkorders,
    };
  }, [installations, isCustomerPortalUser, portalSummary, workorders]);

  const latestInstallations = installations.slice(0, 5);
  const latestWorkorders = isCustomerPortalUser ? workorders.slice(0, 5) : [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-blue-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-6xl space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Logboek24 klantportaal</p>
            <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
            <p className="text-sm text-slate-600">Actuele stand van werkbonnen</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!isCustomerPortalUser ? (
              <Link
                to="/portaal/installaties"
                className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
              >
                Bekijk installaties
              </Link>
            ) : null}
            {isCustomerPortalUser ? (
              <Link
                to="/portaal/berichten"
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Bekijk berichten
              </Link>
            ) : null}
            {isCustomerPortalUser ? (
              <Link
                to="/portaal/profiel"
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Profiel
              </Link>
            ) : null}
            {isCustomerPortalUser ? (
              <Link
                to="/portaal/aanvragen"
                className="rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
              >
                Nieuwe aanvraag
              </Link>
            ) : null}
            {isCustomerPortalUser ? (
              <Link
                to="/portaal/leveranciers"
                className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-800"
              >
                Bekijk leveranciers
              </Link>
            ) : null}
            <Link
              to="/portaal/werkbonnen"
              className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800"
            >
              Bekijk werkbonnen
            </Link>
          </div>
        </header>

        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
        )}

        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard label="Installaties" value={summary.installationCount} tone="slate" />
          <SummaryCard label="Locaties" value={summary.locationCount} tone="amber" />
          <SummaryCard label="Open werkbonnen" value={summary.openWorkorders} tone="blue" />
          <SummaryCard label="Afgeronde werkbonnen" value={summary.doneWorkorders} tone="emerald" />
        </section>

        {isCustomerPortalUser ? (
          <section className={CARD_CLASS}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900">Laatste werkbonnen</h2>
              <Link to="/portaal/werkbonnen" className="text-sm font-medium text-blue-700 hover:underline">
                Alles tonen
              </Link>
            </div>

            {loading ? (
              <p className="text-sm text-slate-500">Laden...</p>
            ) : latestWorkorders.length === 0 ? (
              <p className="text-sm text-slate-500">Nog geen werkbonnen gevonden.</p>
            ) : (
              <div className="space-y-2">
                {latestWorkorders.map((item) => (
                  <Link
                    key={String(item?.id || item?._id || "")}
                    to={`/portaal/werkbonnen/${item?.id || item?._id}`}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2 hover:border-blue-300 hover:bg-blue-50"
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{item?.workorderNumber || item?.werkbonNummer || "-"}</p>
                      <p className="text-xs text-slate-600">{item?.title || item?.omschrijving || "Werkbon"}</p>
                    </div>
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">
                      {item?.statusLabel || formatStatus(item?.status)}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </section>
        ) : (
          <section className={CARD_CLASS}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900">Laatste installaties</h2>
              <Link to="/portaal/installaties" className="text-sm font-medium text-blue-700 hover:underline">
                Alles tonen
              </Link>
            </div>

            {loading ? (
              <p className="text-sm text-slate-500">Laden...</p>
            ) : latestInstallations.length === 0 ? (
              <p className="text-sm text-slate-500">Nog geen installaties gevonden.</p>
            ) : (
              <div className="space-y-2">
                {latestInstallations.map((item) => (
                  <Link
                    key={String(item?._id || item?.id || "")}
                    to={`/portaal/installaties/${item._id}`}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 px-3 py-2 hover:border-blue-300 hover:bg-blue-50"
                  >
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{item?.naam || item?.name || "Onbekend"}</p>
                      <p className="text-xs text-slate-600">
                        {item?.type || "-"} - {item?.locatie || item?.bagAddress?.formattedAddress || "Locatie onbekend"}
                      </p>
                    </div>
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-700">
                      Open
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, tone }) {
  const toneClass =
    tone === "amber"
      ? "bg-amber-50 text-amber-800 border-amber-200"
      : tone === "emerald"
        ? "bg-emerald-50 text-emerald-800 border-emerald-200"
        : tone === "blue"
          ? "bg-blue-50 text-blue-800 border-blue-200"
          : "bg-slate-50 text-slate-800 border-slate-200";

  return (
    <article className={`${CARD_CLASS} ${toneClass}`}>
      <p className="text-xs uppercase tracking-wide">{label}</p>
      <p className="mt-2 text-3xl font-bold">{value}</p>
    </article>
  );
}

function isWorkorderVisibleForClientHub(item) {
  return Boolean(
    item?.workorderTypeFlow?.publishToClientHub ||
      item?.completionFlow?.publishToClientHub ||
      item?.publishToClientHub
  );
}

function formatStatus(status) {
  const value = String(status || "").trim().toLowerCase();
  if (!value) return "-";
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

async function loadInternalWorkorders() {
  try {
    const res = await api.get("/werkbonnen");
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    const fallbackRes = await api.get("/werkbonnen/mine");
    return Array.isArray(fallbackRes?.data) ? fallbackRes.data : [];
  }
}
